import { assertUnreachable, delayPromise, groupByToMultimap, IMultimap } from '@tableau/vizportalclient-common-components';

import { canSubmitLabel } from '~src/catalog/components/dataQuality/canSubmitLabel';
import { JsonRestApiRequestConfig, makeJsonRestApiRequest } from '~src/httpClient/RestApiService';
import { ERROR_NOTIFYING_SERVER_RESPONSE_DELAY } from '~src/httpClient/WebHttpClientWithErrorToast';

import { Label, LabelContentItem, RestLabelResponse, RestLabelResponseWrapper } from './LabelTypes';

const partialConfig: Pick<JsonRestApiRequestConfig, 'restApiVersion' | 'serverWideRequest' | 'endpoint'> = {
  restApiVersion: '3.16',
  serverWideRequest: false,
  endpoint: 'labels',
};

export type SharedLabelParams = {
  readonly category: Label['category'];
  readonly contentItems: ReadonlyArray<LabelContentItem>;
};

export type GetLabelsParams = {
  readonly resolveOwners?: boolean;
} & SharedLabelParams;

export type LabelsKey = {
  readonly contentId: string;
  readonly contentType: string;
} & Object;

export async function getLabels<T>(
  params: GetLabelsParams & {
    readonly transform: (response: RestLabelResponse) => T;
  }
): Promise<IMultimap<LabelsKey, T>> {
  const { resolveOwners, transform } = params;

  if (resolveOwners) {
    throw new Error('Resolve owners is not implemented, getLabels should only be used for the actions menu');
  }

  const response = await Get.getLabelsSparse(params);
  const data = response.labelList.label;
  const labels = groupByToMultimap<RestLabelResponse, LabelsKey, T>({
    data,
    keyTransform: (original) => ({ contentId: original.contentId, contentType: original.contentType.toLowerCase() } as LabelsKey),
    valTransform: transform,
    keyBy: ({contentId, contentType}) => `${contentType}-${contentId}`,
  });
  return labels;
}

namespace Get {
  const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'POST' };

  /**
   * getLabelsSparse is intended to be used for the set warning|certification action and 
   * doesn't resolve user information because that is not required for the action
   * 
   * @param params The list of assets to be queried for and the optional category to filtery by
   * @returns A collection of unresolved labels, i.e.: there is no owner or asset information
   */
  export function getLabelsSparse(params: Pick<GetLabelsParams, 'category' | 'contentItems'>): Promise<RestLabelResponseWrapper> {
    const request = contentItemsToContentList(params);
    const queryParams = makeLabelsQueryParams(params);
    const result = makeJsonRestApiRequest<LabelsContentList, RestLabelResponseWrapper>({ config, request, queryParams });
    return result;
  }
}

export type UpdateOrDeleteLabelParams = {
  readonly luid?: string;
  readonly labelContent: Pick<Label, 'message' | 'active' | 'elevated' | 'value'>;
} & SharedLabelParams;

export async function setOrDeleteLabel(params: UpdateOrDeleteLabelParams): Promise<{}> {
  const { luid, labelContent } = params;
  const command = canSubmitLabel({ active: labelContent?.active ?? false, message: labelContent?.message ?? '' });

  switch (command) {
    case 'DELETE':
      if (luid) {
        return Delete.deleteLabel({ luid });
      } else {
        return Delete.deleteLabels(params);
      }
    case 'SUBMIT':
      return Put.setLabels(params);
    case 'TOOLONG':
      throw new Error('The label message was too long to submit to the server');
    case 'NOMESSAGE':
      throw new Error('A message is required for a label to be submitted');
    default:
      assertUnreachable(command);
  }
}

namespace Put {
  const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'PUT' };

  export function setLabels(params: UpdateOrDeleteLabelParams): Promise<RestLabelResponse> {
    const queryParams = makeLabelsQueryParams(params);
    const request: SetLabelsBody = {
      ...contentItemsToContentList(params),
      label: {
        ...params.labelContent
      }
    };

    const result = makeJsonRestApiRequest<SetLabelsBody, RestLabelResponse>({ config, request, queryParams })
    .then(async (t) => {
      await delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
      return t;
    });

    return result;
  }

  type SetLabelsBody = {
    readonly label: UpdateOrDeleteLabelParams['labelContent'];
  } & LabelsContentList;
}

namespace Delete {
  export async function deleteLabels(params: UpdateOrDeleteLabelParams): Promise<{}> {
    const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'DELETE', endpoint: `labels` };
    const queryParams = makeLabelsQueryParams(params);
    const request = contentItemsToContentList(params);

    const result = makeJsonRestApiRequest<LabelsContentList, {}>({ config, request, queryParams });
     delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);

    return result;
  }

  export async function deleteLabel({ luid }: DeleteLabelParams): Promise<{}> {
    const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'DELETE', endpoint: `labels/${luid}` };

    const result = await makeJsonRestApiRequest<void, {}>({ config, request: undefined }).then((t) => {
      delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
      return t;
    });

    return result;
  }

  type DeleteLabelParams = {
    readonly luid: string;
  }
}

type LabelsContentList = {
  readonly contentList: {
    readonly content: ReadonlyArray<{
      readonly contentType: LabelContentItem['contentType'];
      readonly id: string;
    }>;
  };
}

function contentItemsToContentList(params: Pick<UpdateOrDeleteLabelParams, 'contentItems'>): LabelsContentList {
  return {
    contentList: {
      content: params.contentItems.map(i => ({ id: i.contentId, contentType: i.contentType }))
    }
  };
}

function makeLabelsQueryParams(params: { category?: Label['category'] }) {
  const { category } = params;

  if (!category) {
    return undefined;
  }

  return {
    categories: category
  };
}
